/* oxlint-disable lingui/no-unlocalized-strings -- TODO(i18n): translate (debt) */
import { Trash2 } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Card, CardContent, Switch } from "@fleet/ui";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import type { AdminFontRow } from "@time-tracker/shared";

interface FontCardProps {
  font: AdminFontRow;
  onToggleActive: (id: string, active: boolean) => void;
  onDeleteConfirm: (id: string) => void;
  /**
   * Pre-existing i18n debt (see the disable above): this panel translates with flat
   * strings, not macros — the Lingui extractor never sees them, so they remain in
   * English in the 10 non-English locales. The `useI18n().t` type barrier does not
   * allow a string here; `tDynamic` is the EXPLICIT escape hatch so as not to pretend
   * this is already translated. Do not convert it to `t` without first wrapping
   * every literal in `msg\`…\`` and giving it a catalog — that is the real debt, not this signature.
   */
  tDynamic: (s: string, v?: Record<string, unknown>) => string;
}

export function FontCard(props: FontCardProps) {
  const handleToggleChange = (v: boolean) => {
    props.onToggleActive(props.font.id, v);
  };

  const handleDeleteConfirm = () => {
    props.onDeleteConfirm(props.font.id);
  };

  return (
    <Card class="overflow-hidden group">
      <CardContent class="p-4 flex items-center justify-between">
        <div class="flex items-center gap-4">
          <div class="h-12 w-12 rounded bg-muted flex items-center justify-center text-2xl font-serif">
            Aa
          </div>
          <div>
            <h3 class="font-bold text-lg">{props.font.family}</h3>
            <p class="text-xs text-muted-foreground">
              {props.font.category} •{" "}
              {props.tDynamic("{{count}} variants", { count: props.font.variants.length })}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-4">
          <div class="flex flex-col items-end gap-1">
            <span class="text-[10px] uppercase font-bold text-muted-foreground">
              {props.tDynamic("Active")}
            </span>
            <Switch checked={props.font.isActive} onCheckedChange={handleToggleChange} />
          </div>
          <ConfirmDialog
            title={props.tDynamic("Delete font?")}
            description={props.tDynamic(
              'Are you sure you want to delete the font "{{family}}"? This action cannot be undone.',
              { family: props.font.family },
            )}
            confirmLabel={props.tDynamic("Delete")}
            variant="destructive"
            onConfirm={handleDeleteConfirm}
            renderTrigger={(open) => (
              <Button
                variant="ghost"
                size="icon"
                class="text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={open}
              >
                <Trash2 class="h-4 w-4" />
              </Button>
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
}
